import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';


const BulkAssignModal = ({ selectedTickets, technicians, assignments, onAssign, onClose, detectConflicts }) => {
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [duration, setDuration] = useState('2');
  const [conflicts, setConflicts] = useState([]);
  const [searchTech, setSearchTech] = useState('');

  useEffect(() => {
    if (selectedTechnician && scheduledDate) {
      const dateTimeStr = `${scheduledDate}T${scheduledTime}:00`;
      const detected = detectConflicts(selectedTechnician?.id, dateTimeStr, parseFloat(duration || 2));
      setConflicts(detected);
    } else {
      setConflicts([]);
    }
  }, [selectedTechnician, scheduledDate, scheduledTime, duration]);

  const filteredTechs = technicians?.filter(t =>
    t?.name?.toLowerCase()?.includes(searchTech?.toLowerCase())
  );

  const handleSubmit = () => {
    if (!selectedTechnician) return;
    const dateTimeStr = scheduledDate ? `${scheduledDate}T${scheduledTime}:00` : null;
    onAssign(selectedTechnician, dateTimeStr, duration);
  };

  const getPriorityStyle = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-error/10 text-error';
      case 'high': return 'bg-warning/10 text-warning';
      case 'medium': return 'bg-primary/10 text-primary';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] flex flex-col shadow-elevation-3">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-lg font-heading font-semibold text-foreground">Bulk Technician Assignment</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Assigning {selectedTickets?.length} ticket{selectedTickets?.length !== 1 ? 's' : ''} to a technician
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} color="currentColor" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Selected Tickets Summary */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Icon name="Ticket" size={16} color="var(--color-primary)" />
              Selected Tickets
            </h3>
            <div className="bg-muted/50 rounded-lg p-3 max-h-32 overflow-y-auto space-y-1.5">
              {selectedTickets?.map(ticket => (
                <div key={ticket?.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">{ticket?.id}</span>
                    <span className="text-muted-foreground truncate max-w-48">{ticket?.customer || ticket?.issue}</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityStyle(ticket?.priority)}`}>
                    {ticket?.priority?.charAt(0)?.toUpperCase() + ticket?.priority?.slice(1)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Schedule */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Calendar" size={16} color="var(--color-primary)" />
              Schedule (Optional)
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <label className="block text-xs font-medium text-muted-foreground mb-1">Date</label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={e => setScheduledDate(e?.target?.value)}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="col-span-1">
                <label className="block text-xs font-medium text-muted-foreground mb-1">Time</label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={e => setScheduledTime(e?.target?.value)}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="col-span-1">
                <label className="block text-xs font-medium text-muted-foreground mb-1">Duration (hrs)</label>
                <input
                  type="number"
                  min="0.5"
                  max="12"
                  step="0.5"
                  value={duration}
                  onChange={e => setDuration(e?.target?.value)}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            </div>
          </div>

          {/* Conflict Warning */}
          {conflicts?.length > 0 && (
            <div className="bg-error/10 border border-error/30 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={16} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-error">Scheduling Conflict Detected</p>
                  <p className="text-xs text-error/80 mt-0.5 mb-2">
                    {selectedTechnician?.name} already has {conflicts?.length} assignment{conflicts?.length !== 1 ? 's' : ''} overlapping this time slot:
                  </p>
                  <ul className="space-y-1">
                    {conflicts?.map(conflict => (
                      <li key={conflict?.id} className="text-xs text-error/80 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-error flex-shrink-0" />
                        <span className="font-medium">{conflict?.ticketId}</span>
                        <span>—</span>
                        <span>{new Date(conflict?.scheduledDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {conflict?.scheduledTime} ({conflict?.estimatedDuration}h)</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-error/70 mt-2">You can still proceed, but this may cause a double-booking.</p>
                </div>
              </div>
            </div>
          )}

          {/* Technician Selection */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Users" size={16} color="var(--color-primary)" />
              Select Technician
            </h3>
            <div className="relative mb-3">
              <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search technicians..."
                value={searchTech}
                onChange={e => setSearchTech(e?.target?.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div className="space-y-2 max-h-52 overflow-y-auto">
              {filteredTechs?.length > 0 ? filteredTechs?.map(tech => {
                const isSelected = selectedTechnician?.id === tech?.id;
                const techConflicts = scheduledDate
                  ? detectConflicts(tech?.id, `${scheduledDate}T${scheduledTime}:00`, parseFloat(duration || 2))
                  : [];
                const hasConflict = techConflicts?.length > 0;

                return (
                  <div
                    key={tech?.id}
                    onClick={() => setSelectedTechnician(tech)}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-smooth ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : hasConflict
                        ? 'border-error/30 bg-error/5 hover:border-error/50' :'border-border hover:border-primary/40 hover:bg-muted/50'
                    }`}
                  >
                    <div className="relative flex-shrink-0">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-muted">
                        <Image
                          src={tech?.avatar}
                          alt={tech?.avatarAlt}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card ${
                        tech?.status === 'available' ? 'bg-success' :
                        tech?.status === 'busy' ? 'bg-warning' : 'bg-muted'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground truncate">{tech?.name}</p>
                        {hasConflict && (
                          <span className="flex items-center gap-1 text-xs text-error font-medium flex-shrink-0">
                            <Icon name="AlertTriangle" size={12} color="currentColor" />
                            Conflict
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground capitalize">{tech?.status} • {tech?.activeJobs} active jobs</p>
                    </div>
                    {isSelected && (
                      <div className="flex-shrink-0">
                        <Icon name="CheckCircle" size={18} color="var(--color-primary)" />
                      </div>
                    )}
                  </div>
                );
              }) : (
                <div className="text-center py-6 text-sm text-muted-foreground">
                  No technicians found
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-border flex-shrink-0 bg-muted/30">
          <div className="text-sm text-muted-foreground">
            {selectedTechnician ? (
              <span className="flex items-center gap-1.5">
                <Icon name="User" size={14} color="currentColor" />
                Assigning to <span className="font-medium text-foreground">{selectedTechnician?.name}</span>
                {conflicts?.length > 0 && (
                  <span className="text-error font-medium">({conflicts?.length} conflict{conflicts?.length !== 1 ? 's' : ''})</span>
                )}
              </span>
            ) : (
              <span>Select a technician to continue</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              variant={conflicts?.length > 0 ? 'destructive' : 'default'}
              onClick={handleSubmit}
              disabled={!selectedTechnician}
              iconName={conflicts?.length > 0 ? 'AlertTriangle' : 'CheckCircle'}
              iconPosition="left"
            >
              {conflicts?.length > 0 ? 'Assign Anyway' : `Assign ${selectedTickets?.length} Ticket${selectedTickets?.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignModal;